import { MOVEMENT_SERVICE_STATUSES } from "@features/vessel-call/vessel-call-status/MovementServices/flow/utils.tsx";
import { useAuthStore } from "@persisted-state/auth/auth.store";

export const VESSEL_CALL_STATUSES = {
  PENDING_PRE_APPROVAL: "PENDING_PRE_APPROVAL", // MS are task
  PRE_ARRIVAL_DRAFT: "PRE_ARRIVAL_DRAFT",
  PRE_APPROVED: "PRE_APPROVED", // SA va face submit
  PENDING_APPROVAL: "PENDING_APPROVAL", // MO va avea task
  APPROVED: "APPROVED",
  ARRIVAL_DRAFT: "ARRIVAL_DRAFT",
  REJECTED_MARINE_SUPERVISOR: "REJECTED_MARINE_SUPERVISOR",
  REJECTED_MARINE_OPERATOR: "REJECTED_MARINE_OPERATOR",
  RESUME_PENDING: "RESUME_PENDING",
  EXPIRED: "EXPIRED",
  INTERRUPTED_IN_PORT: "INTERRUPTED_IN_PORT",
  INTERRUPTED_LEAVE_PORT: "INTERRUPTED_LEAVE_PORT",
  CLOSED: "CLOSED",
  REINSTATED: "REINSTATED",
  DEPARTED: "DEPARTED",
};

export const TASK_STATUSES = {
  READY: "READY",
  CLAIMED: "CLAIMED",
  COMPLETED: "COMPLETED",
  CANCELLED: "CANCELLED",
  TERMINATED: "TERMINATED",
};

export const CLAIMABLE_VESSEL_CALL_STATUSES = [
  VESSEL_CALL_STATUSES.PENDING_PRE_APPROVAL,
  VESSEL_CALL_STATUSES.PENDING_APPROVAL,
  VESSEL_CALL_STATUSES.RESUME_PENDING,
];
export const UPDATEBLE_VESSEL_CALL_STATUSES = [
  VESSEL_CALL_STATUSES.REJECTED_MARINE_SUPERVISOR,
  VESSEL_CALL_STATUSES.REJECTED_MARINE_OPERATOR,
];

export const INTERRUPTION_STATUSES = [
  VESSEL_CALL_STATUSES.INTERRUPTED_IN_PORT,
  VESSEL_CALL_STATUSES.INTERRUPTED_LEAVE_PORT,
];

export const DRAFT_STATUSES = [
  VESSEL_CALL_STATUSES.ARRIVAL_DRAFT,
  VESSEL_CALL_STATUSES.PRE_ARRIVAL_DRAFT,
];

export const CANCELABLE_STATUSES = [
  VESSEL_CALL_STATUSES.PENDING_PRE_APPROVAL,
  VESSEL_CALL_STATUSES.PRE_APPROVED,
  VESSEL_CALL_STATUSES.PENDING_APPROVAL,
  VESSEL_CALL_STATUSES.APPROVED,
];

export const CANCELABLE_STATUSES_FOR_SECURITY_OPERATOR = [
  VESSEL_CALL_STATUSES.PRE_APPROVED,
  VESSEL_CALL_STATUSES.PENDING_APPROVAL,
  VESSEL_CALL_STATUSES.APPROVED,
];
const PRE_APPROVABLE_VESSEL_CALL_STATUSES = [VESSEL_CALL_STATUSES.PENDING_PRE_APPROVAL];
const APPROVABLE_VESSEL_CALL_STATUSES = [VESSEL_CALL_STATUSES.PENDING_APPROVAL];

// Vessel Call methods
const isVesselCallClaimable = (status: string) => CLAIMABLE_VESSEL_CALL_STATUSES.includes(status);

const isVesselCallPreApprovableOrRejectable = (status: string) =>
  PRE_APPROVABLE_VESSEL_CALL_STATUSES.includes(status);

const isVesselCallUpdateble = (status: string) => UPDATEBLE_VESSEL_CALL_STATUSES.includes(status);

const isVesselCallApprovableOrRejectable = (status: string) =>
  APPROVABLE_VESSEL_CALL_STATUSES.includes(status);

// Task methods
const isTaskNotClaimed = (taskStatus: string) => taskStatus === TASK_STATUSES.READY;
const isTaskClaimed = (taskStatus: string) => taskStatus === TASK_STATUSES.CLAIMED;

const isTaskOwner = (claimOwnerName: string) =>
  claimOwnerName === useAuthStore.getState().user?.preferredUsername;

const isUserClaimedTaskOwner = (taskStatus: string, claimOwnerName: string) =>
  isTaskClaimed(taskStatus) && isTaskOwner(claimOwnerName);

// Flow methods
export const canUserClaim = (taskStatus: string, status: string) => {
  return isTaskNotClaimed(taskStatus) && isVesselCallClaimable(status);
};

export const canUserUnclaim = (taskStatus: string, claimOwnerName: string) =>
  isUserClaimedTaskOwner(taskStatus, claimOwnerName);

export const canShowCloseVesselCallActions = (
  vesselCallStatus: string | undefined,
  movements: Array<{ movementReason?: string; status: string }>,
) => {
  if (vesselCallStatus !== VESSEL_CALL_STATUSES.DEPARTED) return false;

  return !movements.some(
    (movement) =>
      movement.movementReason?.trim().toUpperCase() === "REINSTATEMENT" &&
      movement.status.trim().toUpperCase() === "APPROVED",
  );
};

const MARINE_SUPERVISOR_ROLE = "marine-supervisor";

const isMarineSupervisorRole = () => {
  const userRoles = useAuthStore.getState().user?.roles ?? [];
  return userRoles.includes(MARINE_SUPERVISOR_ROLE);
};

export const canUserClaimResumeCall = (taskStatus: string, status: string) =>
  canUserClaim(taskStatus, status) && isMarineSupervisorRole();

export const canUserUnclaimResumeCall = (taskStatus: string, claimOwnerName: string) =>
  canUserUnclaim(taskStatus, claimOwnerName);

export const canUserResume = (status: string) => {
  return status === VESSEL_CALL_STATUSES.RESUME_PENDING;
};

export const canUserRejectResumption = (status: string) => {
  return status === VESSEL_CALL_STATUSES.RESUME_PENDING;
};

export const canUserPreApprove = (taskStatus: string, claimOwnerName: string, status: string) => {
  return (
    isUserClaimedTaskOwner(taskStatus, claimOwnerName) &&
    isVesselCallPreApprovableOrRejectable(status)
  );
};

export const canUserApprove = (taskStatus: string, claimOwnerName: string, status: string) => {
  return (
    isUserClaimedTaskOwner(taskStatus, claimOwnerName) && isVesselCallApprovableOrRejectable(status)
  );
};

export const canUserPreReject = (taskStatus: string, claimOwnerName: string, status: string) => {
  return (
    isUserClaimedTaskOwner(taskStatus, claimOwnerName) &&
    isVesselCallPreApprovableOrRejectable(status)
  );
};

export const canUserUpdate = (status: string, companyId?: string) => {
  if (!companyId) return false;
  return (
    isVesselCallUpdateble(status) && useAuthStore.getState().user?.companyId?.includes(companyId)
  );
};

export const canUserReject = (taskStatus: string, claimOwnerName: string, status: string) => {
  return (
    isUserClaimedTaskOwner(taskStatus, claimOwnerName) && isVesselCallApprovableOrRejectable(status)
  );
};

export const canUserInterrupt = (status: string) => {
  return status === VESSEL_CALL_STATUSES.APPROVED;
};

export const canUserResumeInterruption = (status: string, companyId?: string) => {
  const userCompany = useAuthStore.getState().user?.companyId ?? [];
  return INTERRUPTION_STATUSES.includes(status) && companyId === userCompany;
};

export const canUserResumeResumption = (taskStatus: string, claimOwnerName: string) => {
  return isUserClaimedTaskOwner(taskStatus, claimOwnerName);
};

export const canDiscardOrEditDraft = (status: string) => {
  return (
    status === VESSEL_CALL_STATUSES.ARRIVAL_DRAFT ||
    status === VESSEL_CALL_STATUSES.PRE_ARRIVAL_DRAFT
  );
};

export const canUserCancel = (
  status: string,
  hasMovementAnchorTimeOrAllFast: boolean,
  movementStatus?: string,
  roles?: string[],
) => {
  if (roles?.includes("marine-operator")) {
    return (
      !hasMovementAnchorTimeOrAllFast && CANCELABLE_STATUSES_FOR_SECURITY_OPERATOR.includes(status)
    );
  }

  return (
    !hasMovementAnchorTimeOrAllFast &&
    (CANCELABLE_STATUSES.includes(status) ||
      movementStatus === MOVEMENT_SERVICE_STATUSES.MOVEMENT_STARTED)
  );
};
